/**
 * outputFilename — PURE helpers behind the AI Review "Output file" field
 * (sub-task 03 R1.3–R1.5, R1.9). No I/O, no React: the probe query itself
 * lives in `useOutputFilenameSuggestion.ts`, this module only decides what a
 * name means.
 *
 * The convention is the approved Option C mock's:
 * `<stem>_AI_review_v<N>.csv` (`ai_review_versions_optionC.html:265`), and it
 * is load-bearing: the version series is a NAME namespace, one per base stem.
 * `parseReviewVersion` is therefore ANCHORED to the stem at BOTH ends. The
 * server's `q` is a case-insensitive substring over `original_filename` OR
 * `search_name_cached` (`app/web/v2/imports/repository.py:249-262`), so a probe
 * page legitimately contains rows that merely *contain* the stem and rows
 * matched on their search name; the anchoring is what excludes those.
 *
 * What it deliberately does NOT exclude, since 2026-08-25: a row belonging to a
 * DIFFERENT import that anchors to the same stem. Q1 filtered those on lineage,
 * and chaining made that impossible to keep — see
 * `suggestOutputFilenameFromRows`. A row can only be named
 * `<our-stem>_AI_review_v<N>` if its own parent shares our filename, in which
 * case the namespace really is shared and counting it AVOIDS a clash.
 */

/** The version marker the convention puts between the stem and `<N>`. */
const REVIEW_MARKER = "_AI_review_v";

/** The default extension. Overridden by the PARENT's, because the child's body
 *  IS the parent's object with the review appended — a `.csv` suggestion over a
 *  JSON parent would quote a name in the confirm dialog that the file never
 *  ends up carrying, since the server corrects the extension to match the
 *  format. (The recruiter may still delete it — R1.8 accepts a name without
 *  one.) */
const CSV_EXTENSION = ".csv";

/** The extension the derived file will actually have, given its parent's name.
 *
 *  Deliberately keyed on the parent's DISPLAY name rather than its S3 key: the
 *  key is not on the wire here. The server has the key and is the authority —
 *  it force-corrects the extension either way — so the worst this can do is
 *  suggest a name the server then adjusts, which is the failure mode that
 *  already existed. Getting it right for the common case stops the dialog
 *  quoting a name the file will not have.
 */
function extensionFor(parentFilename: string): string {
  return parentFilename.toLowerCase().endsWith(".json") ? ".json" : CSV_EXTENSION;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * The filename minus its FINAL extension. A dotless name is returned as-is; a
 * leading dot is not an extension (`.hidden` stays `.hidden`).
 */
export function stemOf(filename: string): string {
  const dot = filename.lastIndexOf(".");
  return dot > 0 ? filename.slice(0, dot) : filename;
}

/** Matches a trailing `_AI_review_v<N>`, the marker {@link seriesStemOf} strips. */
const TRAILING_REVIEW_MARKER = new RegExp(`${escapeRegExp(REVIEW_MARKER)}\\d+$`, "i");

/**
 * The stem a review of `filename` should be VERSIONED AGAINST.
 *
 * For an ordinary import this is just {@link stemOf}. For a file that is itself
 * a review — which became reviewable on 2026-08-25 (Trello #711 cl.5, the D5
 * reversal) — it is the stem with the trailing `_AI_review_v<N>` stripped, so
 * a review of `Bankers_AI_review_v1.csv` is named `Bankers_AI_review_v2.csv`
 * and JOINS the series rather than nesting into
 * `Bankers_AI_review_v1_AI_review_v1.csv`.
 *
 * Joining the series is what keeps the rest of this module working on a chained
 * run. `reviewProbeQuery`, `parseReviewVersion` and the max-based suggestion
 * are all anchored to one stem; a nested name is invisible to every one of
 * them, so the next chained review would re-mint `v1` under a third stem, and
 * so on. One strip, not a loop: the marker cannot legitimately repeat once
 * every generation is named off the same base.
 *
 * Only the NAME is flattened. Lineage is unaffected: `parent_import_id` still
 * points at the review that was scored. It is no longer consulted for the
 * version max, though — see `suggestOutputFilenameFromRows` for why a flat
 * series forces a namespace-wide max.
 */
export function seriesStemOf(filename: string): string {
  return stemOf(filename).replace(TRAILING_REVIEW_MARKER, "");
}

/**
 * `<N>` from a review file belonging to `stem`, or `null` when the name is not
 * a review of exactly that stem.
 *
 * Case-insensitive (the probe's `q` is), and tolerant of a missing/other
 * extension so a hand-edited name still participates in the max. Anything
 * after the digits other than a single extension disqualifies the row.
 */
export function parseReviewVersion(filename: string, stem: string): number | null {
  const pattern = new RegExp(
    `^${escapeRegExp(stem)}${escapeRegExp(REVIEW_MARKER)}(\\d+)(\\.[^.]*)?$`,
    "i"
  );
  const match = pattern.exec(filename.trim());
  if (!match?.[1]) return null;
  return Number.parseInt(match[1], 10);
}

/**
 * The R1.4 probe's `q`: the stem plus the version marker. A substring, so the
 * server returns every version of this import — plus unrelated rows the two
 * `parse`/`isDuplicate` filters below discard.
 */
export function reviewProbeQuery(parentFilename: string): string {
  return `${seriesStemOf(parentFilename)}${REVIEW_MARKER}`;
}

/** The subset of an `ImportSummary` the version count reads. */
export interface ReviewProbeRow {
  filename: string;
  /** True lineage, shipped on `ImportSummary` by packet 2.5 (❓Q1 / R19).
   *
   *  NOT read by the version max any more (a flat name series forces a
   *  namespace-wide max — see `suggestOutputFilenameFromRows`). Kept on the
   *  row type because callers pass whole `ImportSummary`-shaped objects and
   *  narrowing it would buy nothing; flagged here so it is not mistaken for a
   *  live input (`/pr-review` iter 2, Agent B N3). */
  parent_import_id?: string | null;
}

/**
 * `<stem>_AI_review_v<N>.csv` where `<N>` is `max(existing versions) + 1`,
 * defaulting to `1`. Deliberately max-based rather than count-based: deleting
 * `v2` must not make the next run re-mint `v2`.
 *
 * Row-aware form. `suggestOutputFilename` below is the name-only wrapper, kept
 * because the duplicate check and the pure tests have no rows — and now that
 * lineage plays no part in the max, the two forms differ only in convenience.
 */
export function suggestOutputFilenameFromRows(
  parentFilename: string,
  rows: readonly ReviewProbeRow[]
): string {
  const stem = seriesStemOf(parentFilename);
  // Seed from the PARENT'S OWN version when the parent is itself a review file.
  //
  // The probe is advisory and degrades to an empty page (a 5xx, or a stem that
  // matches nothing). With a flat series that degraded case is no longer safe on
  // its own: chaining off `X_AI_review_v1.csv` with no rows would compute max=0
  // and mint `X_AI_review_v1.csv` — the parent's own name. Under the old nested
  // naming the collision was impossible because the child got a new stem.
  //
  // The parent is a member of the series by definition, so counting it costs
  // nothing when the probe DOES return it (the max is a max) and is the only
  // thing standing between a failed probe and a duplicate when it does not.
  let max = parseReviewVersion(parentFilename, stem) ?? 0;
  for (const row of rows) {
    // Membership is decided by the ANCHORED NAME alone, for every parent —
    // lineage plays no part in the version max any more.
    //
    // ❓Q1 made this arm lineage-precise, and that was right while the name
    // series and the lineage tree had the same shape: one series per parent,
    // because only an original could be reviewed. Chaining broke that
    // correspondence. A chained review JOINS its parent's series by name
    // (`seriesStemOf`) while hanging off a different parent, so a
    // lineage-scoped max cannot see it — and `/pr-review` iter 1 (Agent C, I1)
    // reproduced the consequence by execution:
    //
    //   X.csv ──> X_AI_review_v1.csv ──(chained)──> X_AI_review_v2.csv
    //   then re-reviewing X.csv again also mints X_AI_review_v2.csv
    //
    // Two different files, one name, and `isDuplicateFilename` is advisory
    // only — nothing stops it.
    //
    // You cannot have a FLAT name series and LINEAGE-SCOPED numbering at once.
    // The name asserts one namespace per stem, so the max has to be computed
    // over that same namespace. What this gives up is ❓Q1's precision: a
    // similarly-named file from an unrelated import can now raise the max. That
    // costs a SKIPPED version number. The alternative costs a collision, and
    // `parseReviewVersion` is anchored at both ends (the probe's `q` is
    // stem-scoped too), so the overlap is narrow.
    //
    // Lineage itself is untouched — `parent_import_id` still records the true
    // tree. Only the NUMBERING is namespace-wide. `reviewVersionOfRow`, which
    // encoded the lineage-scoped rule, is DELETED rather than left unused: a
    // dead helper implementing the superseded rule is an invitation to restore
    // the collision.
    const version = parseReviewVersion(row.filename, stem);
    if (version !== null && version > max) max = version;
  }
  return `${stem}${REVIEW_MARKER}${max + 1}${extensionFor(parentFilename)}`;
}

/**
 * Name-only form: every row is treated as parentless, so the anchored-regex
 * fallback decides. This is what the optimistic seed on import-select uses
 * (it has no probe page yet) and what the pure unit tests exercise.
 */
export function suggestOutputFilename(
  parentFilename: string,
  existingFilenames: readonly string[]
): string {
  return suggestOutputFilenameFromRows(
    parentFilename,
    existingFilenames.map((filename) => ({ filename }))
  );
}

/**
 * Exact (case-insensitive, whitespace-trimmed) match of the typed name against
 * the probe's filenames. Advisory only — R1.5: it never blocks the run.
 */
export function isDuplicateFilename(
  candidate: string,
  existingFilenames: readonly string[]
): boolean {
  const needle = candidate.trim().toLowerCase();
  if (!needle) return false;
  return existingFilenames.some((name) => name.trim().toLowerCase() === needle);
}
